import { selectOption } from './../types/selectOption';
import moment, { Moment } from "moment";
import Discipline from "../api/models/discipline";
import DisciplineTherapists from "../api/models/disciplineTherapists";
import DisciplineUsers from "../api/models/disciplineUsers";
import TimesheetWeek from "../api/models/timesheetWeek";
import CaseloadDisplay from "../api/models/caseloadDisplay";
import CsvData from "../types/csvData";
import AutoCompleteOption from "../api/models/autoCompleteModel";
import { BillToLookup, Role, TaskTypeLookup, studentStatuses } from './constants';
import TimesheetWeekTask from '../api/models/TimesheetWeekTask';
import TimesheetWeekEntry from '../api/models/TimesheetWeekEntry';
import DayEntry from '../components/logTimeModal/types/dayEntry';
import TaskType from '../api/models/tasktype';
import EntryState from '../components/logTimeModal/types/entryState';
import TimesheetTaskEntries from '../components/logTimeModal/types/timesheetTaskEntries';
import UserRole from '../api/models/userrole';

type Rule = {
  text: string;
  validation: () => boolean;
};

type option = { label: string; value: number; };

const passwordValidate = {
  IsValid: (pass: string, confirmPass: string) => {

    const rules: Rule [] = [
      {
        text: 'Between 8 and 20 characters',
        validation: () => {
          return pass.length >= 8 && pass.length <= 20;
        },
      },
      {
        text: 'Has uppercase character',
        validation: () => {
          return /[A-Z]/.test(pass);
        },
      },
      {
        text: 'Has lowercase character',
        validation: () => {
          return /[a-z]/.test(pass);
        },
      },
      {
        text: 'Has a digit',
        validation: () => {
          return /[0-9]/.test(pass);
        },
      },
      {
        text: 'Has special character',
        validation: () => {
          //eslint-disable-next-line
          return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(pass);
        },
      },
      {
        text: 'Password & Confirm Password Match',
        validation: () => {
          return pass === confirmPass && pass.length > 0 && confirmPass.length > 0;
        },
      },
    ];

    let isValid = !rules.some((r) => r.validation() === false);
    return isValid;
  }
};

/**
 * Given a start date this will return an array of days for the next 7 days. This will always start on Monday even if it's on the next month
 * @param startDate 
 * @returns array of moment objects
 */
export const daysOfWeek = (startDate: any) =>{
  //prevent from updating the date object that was passed in
  let date = moment(startDate);

  //if the start date isn't monday we need to adjust
  if(date.isoWeekday() !== 1){
    if(date.isoWeekday() === 7)
      //saturday, we shift day forward
      date.add(1, "days")
    else
      //between tuesday-friday, we shift backwards
      date.add((date.isoWeekday() - 1) * -1, 'days');
  }
  
  var dates = [];
  for(var i = 0; i < 5; i++){
      dates.push(moment(date));
      date = date.add(1, 'days');
  }
  return dates; 
}

export default passwordValidate;

export const getEntryMinutesForDay = (districtId: number, timesheetWeek: TimesheetWeek, dayEntry: DayEntry) => {
  let districtWeekItems = timesheetWeek?.items?.filter(i => i.districtId === districtId);
  let dayEntryMinutesSum = 0;

  districtWeekItems.forEach(item => {
      item.tasks.forEach(task => {
          task.entries.forEach(entry => {
              if (moment(entry.dateOfEntry).isSame(dayEntry.dateOfEntry)) {
                  dayEntryMinutesSum += entry.minutes ? entry.minutes.valueOf() : 0 ?? 0;
              }
          });
      });
  });
  return dayEntryMinutesSum;
}

/**
* Formats a phone number
* @param phoneNumber: string
* @returns string
*/
export const formatPhoneNumber = (phoneNumber: string) => {
var cleaned = ('' + phoneNumber).replace(/\D/g, '');
var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
if (match) {
  return '(' + match[1] + ') ' + match[2] + '-' + match[3];
}
return null;
}

/**
 * Determines if a variable of any type has a value
 * @param arg: any
 * @returns boolean
 */
export const hasValue = (arg: any): boolean => arg && arg!==null && arg!==undefined && arg!=='';

/**
 * Capitalizes the first letter of a string
 * @param str 
 * @returns string
 */
export const UCFirst = (str: string): string => str[0].toUpperCase() + str.substr(1);

/**
 * Lowercases the first letter of a string
 * @param str 
 * @returns string
 */
 export const LCFirst = (str: string): string => str[0].toLowerCase() + str.substr(1);

/**
 * Determines whether or not a variable is of type 'Date'
 * @param date Date | string | null
 * @returns boolean
 */
export const isDate = (date: any): boolean|null => {
  //First, check if the variable is a date object
  if (Object.prototype.toString.call(date) === '[object Date]')
    return true;
  
  //If that fails, most likely, it is a string.
  //In this case, we check using a regex.
  //Format: 2022-10-09T00:00:00
  const dateRegex = /[0-9]*-[0-9]*-[0-9]*T[0-9]*:[0-9]*:[0-9]*/;

  return typeof(date)==='string' ? date?.match(dateRegex)?.length!>0 : null;
}

/**
 * Provides a cleaner way of writing a ternary expression
 * Example:
 *  displayDefault('hello world');
 *    is the same as
 *  value!==null && value!==undefined && value!=='' ? value : 'N/A';
 * @param value 
 * @param defaultValue 
 * @returns 
 */
export const displayDefault = (value: any, defaultValue: any = 'N/A') => {
  return hasValue(value) ? value : defaultValue;
}

/**
 * Maps a user property to a display value
 * @param key 
 * @returns string
 */
export const displayChangesHeading = (key: string): string => {
  const lkey: string = key.toLowerCase();
  let heading: string = key;

  switch(lkey){
    case 'firstname':
      heading = 'First Name';
      break;
    case 'middleinitial':
      heading = 'Middle Initial';
      break;
    case 'lastname':
      heading = 'Last Name';
      break;
    case 'emailaddress':
      heading = 'Email Address';
      break;
    case 'address1':
      heading = 'Address 1';
      break;
    case 'address2':
      heading = 'Address 2';
      break;
    case 'stateid':
      heading = 'State';
      break;
    case 'zipcode':
      heading = 'Zip Code';
      break;
    case 'paymentschedule':
      heading = 'Payment Schedule';
      break;
    case 'cfystartdate':
      heading = 'CFY Start Date';
      break;
    case 'cfyenddate':
      heading = 'CFY End Date';
      break;
    case 'internstartdate':
      heading = 'Intern Start Date';
      break;
    case 'internenddate':
      heading = 'Intern End Date';
      break;
    case 'referringtherapistid':
      heading = 'Referring Therapist';
      break;
    case 'superviseeid':
      heading = 'Supervisee';
      break;
  }

  return UCFirst(heading);
}

/**
 * Returns a Date object with the last friday of a given month, year
 * @param month 
 * @param year 
 * @returns Date
 */
export const getLastFridayOfMonth = (month: number, year: number): Date => {
	var lastDay = new Date(year, month+1, 0);
	if(lastDay.getDay() < 5) {
		lastDay.setDate(lastDay.getDate() - 7);
	}
	lastDay.setDate(lastDay.getDate() - (lastDay.getDay() -5));
	return lastDay;
}

/**
 * Returns a Date object with the last friday of the current month, year
 * @returns Date
 */
export const getLastFridayOfCurrentMonth = (): Date => getLastFridayOfMonth(new Date().getMonth(), new Date().getFullYear())

/**
 * Returns whether the current date is prior to the last friday of the month
 * @returns boolean
 */
export const isTodayBeforeLastFridayOfMonth = (): boolean => new Date().getTime()<getLastFridayOfCurrentMonth().getTime()

/**
 * Sorts users by disciplines
 * @param disciplineTherapists 
 * @returns DisciplineUsers[]
 */
export const sortUsersByDiscipline = (disciplineTherapists: DisciplineTherapists): DisciplineUsers[] => {
  const sorted: DisciplineUsers[] = [];
  disciplineTherapists.disciplines.forEach((d: Discipline) => {
      const disciplineUsers: DisciplineUsers = {
          discipline: d,
          users: disciplineTherapists.therapists.filter(u => u?.therapists?.filter(t => t?.disciplineId===d?.disciplineId)?.length!>0)
      }

      sorted.push(disciplineUsers);
  });
  
  return sorted;
}

export const generateCaseloadsCsv = (
  caseloads: CaseloadDisplay[],
  disciplineId: number,
  filter: string = ''
) => {
  const csvData = caseloads.filter(
    (f) =>
      f && f.disciplineId === disciplineId &&
      (filter === '' ||
        f.studentFirstName.toLowerCase().includes(filter.toLowerCase()) ||
        f.studentLastName.toLowerCase().includes(filter.toLowerCase()) ||
        f.gradeName?.toLowerCase().includes(filter.toLowerCase()) ||
        f.schoolName.toLowerCase().includes(filter.toLowerCase()) ||
        f.districtName.toLowerCase().includes(filter.toLowerCase()) ||
        moment(f.dob?.toString())
          .format('MM/DD/YYYY')
          .includes(filter.toLowerCase()))
  )
  .sort((a, b) => a.studentLastName.localeCompare(b.studentLastName))
  .map((val) => {
    return {
      caseloadId: val.caseloadId,
      studentName: `${val.studentFirstName} ${val.studentLastName}`,
      studentFirstName: val.studentFirstName,
      studentLastName: val.studentLastName,
      disciplines: caseloads.filter((f) => f && f.studentId === val.studentId).map((d) => d.disciplineName).sort((a, b) => a.localeCompare(b)).join(', '),
      district: val.districtName,
      school: val.schoolName,
      dob: moment(val.dob?.toString()).format('MM/DD/YYYY'),
      status: val.studentStatus,
      iepDate: val.iepStartDate !== null && val.iepEndDate !== null ? `${moment(val.iepStartDate?.toString()).format('MM/DD/YYYY')}\n${moment(val.iepEndDate?.toString()).format('MM/DD/YYYY')}` : `None`,
      therapyType: val.therapyType,
      grade: val.gradeName
    } as CsvData;
  });

  return csvData;
}

/**
 * Generates a list of grades from 1 to the max (default of 12)
 * @param max The upper limit of the grade options
 * @returns SelectOption[]
 */
export const generateGradeOptions = (max = 12) => {
  const options: selectOption[] = [];
  [...Array(max).keys()].map(i => options.push({ value: (i + 1), label: (i + 1).toString() }));
  options.sort((a, b) => {
    return a.label.localeCompare(b.label);
  });
  return options;
}

/**
 * Generates an array of AutoCompleteOption objects from the given data array.
 * If sort is true, options will be sorted by name.
 * 
 * @param {any[]} data - The array of data objects to generate options from.
 * @param {string} idProp - The property name of the id in each data object.
 * @param {string} nameProp - The property name of the name in each data object.
 * @param {boolean} [sort=false] - Whether or not to sort the options by name.
 * @return {AutoCompleteOption[]} - The array of AutoCompleteOption objects generated.
 */
export const generateAutocompleteOptions = (data: any[], idProp: string, nameProp: string, sort: boolean = false) => {
  const options: AutoCompleteOption[] = [];

  data.map((g) => options.push({ id: g[idProp] ?? -1, name: g[nameProp] }));

  if (sort){
    options.sort((a, b) => a.name.localeCompare(b.name));
  }

  return options;
}

/**
 * Generates an array of select options based on given data, valueProp and labelProp.
 * If sort is true, options will be sorted by label.
 *
 * @param {any[]} data - The data to generate options from.
 * @param {string} valueProp - The property name in data to use as value for options.
 * @param {string} labelProp - The property name in data to use as label for options.
 * @param {boolean} [sort=false] - Whether to sort options by label.
 * @returns {selectOption[]} An array of select options.
 */
export const generateSelectOptions = (data: any[], valueProp: string = 'value', labelProp: string = 'label', sort: boolean = false) => {
  const options: selectOption[] = [];

  data.map((g) => options.push({ value: g[valueProp] ?? -1, label: g[labelProp] }));

  if (sort){
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return options;
}

export const generateOptions = (data: any[], valueProp: string = 'value', labelProp: string = 'label', sort: boolean = false) => {
  const options: option[] = [];

  data.map((g) => options.push({ value: g[valueProp] ?? -1, label: g[labelProp] }));

  if (sort){
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return options;
}

export const dynamicSort = (property: string) => {
  var sortOrder = 1;
  if(property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
  }

  return function (a: any, b: any) {
    var result = (a[property].toLowerCase() < b[property].toLowerCase()) ? -1 : (a[property].toLowerCase() > b[property].toLowerCase()) ? 1 : 0;
    return result * sortOrder;
  }
}

/**
 * Returns the status ID of a student status given its name.
 *
 * @param {string} name - Name of the student status to get the ID for.
 * @return {number} The ID of the student status with the given name, or -1 if the
 * name is not found.
 */
export const getStatusByName = (name: string) => {
  switch(name){
    case studentStatuses.Referral.name:
      return studentStatuses.Referral.studentStatusId;
    case studentStatuses.El.name:
      return studentStatuses.El.studentStatusId;
    case studentStatuses.OnCaseload.name:
      return studentStatuses.OnCaseload.studentStatusId; 
    default:
      return -1;
  }
}

/**
 * Returns an array with all duplicates removed.
 *
 * @param {any[]} data - The array to remove duplicates from.
 * @return {any[]} The array with duplicates removed.
 */
export const removeDuplicatesFromArray = (data: any[]) => {
  return [... new Set(data)];
}

export const removeDuplicateMoments = (data: Moment[]) => {
  let temp: Moment[] = [];
  data.forEach(x => {
    if (temp.every(y => !y.isSame(x)))
     temp.push(x);
  })

  return temp;
}

/**
 * Sorts an array of elements alphabetically or numerically.
 *
 * @param {any[]} arr - an array to be sorted
 * @param {string | null} prop - the property to sort by, if elements are objects
 * @return {any[]} the sorted array
 */
export const sortAlphabetically = (arr: any[], prop: string | null = null): any[] => {
  if (arr && arr.length > 0) {
    if (prop !== null && typeof arr[0] === 'object' && arr[0][prop] !== undefined){
      return arr.sort((a, b) => a[prop].localeCompare(b.name));
    }
  }

  return arr.sort((a, b) => a - b);
}

/**
 * Removes duplicates from an array of objects based on a specified property.
 *
 * @param {T[]} array - The array of objects to remove duplicates from.
 * @param {keyof T} property - The property to check for duplicates.
 * @return {T[]} - The array of objects with duplicates removed.
 */
export const removeDuplicates = <T>(array: T[], property: keyof T): T[] => {
  const unique: {[key: string]: T} = {};
  array.forEach(item => {
      const key = item[property];
      if (typeof key === 'string' || typeof key === 'number') {
          unique[key] = item;
      }
  });
  return Object.values(unique);
}

export const removeDuplicatesManyProperties = <T>(array: T[], properties: Array<keyof T>): T[] => {
  const unique: {[key: string]: T} = {};
  array.forEach(item => {
    const key = properties.map(property => String(item[property])).join('|');
    if (typeof key === 'string') {
      unique[key] = item;
    }
  });
  return Object.values(unique);
}


export const getTimeDisplay = (task: TimesheetWeekTask, day: Moment): string => {
  var entry = task.entries.find(e => moment(e.dateOfEntry).isSame(day));
  if (entry === undefined)
      return "0";
  if (task.taskTypeId === TaskTypeLookup.Absence) {
      return "A";
  }
  if (task.taskTypeId === TaskTypeLookup.Unavailable)
      return "x";

  return entry.minutes?.toString() ?? "0";
}

export const getTimeDisplayByEntry = (entry: TimesheetWeekEntry, taskTypeId: number, day: Moment): string => {
  if (entry === undefined || entry.timesheetEntryId === undefined || (entry.timesheetEntryId !== undefined && entry.timesheetEntryId === 0))
      return "0";
  if (taskTypeId === TaskTypeLookup.Absence) {
      return "A";
  }
  if (taskTypeId === TaskTypeLookup.Unavailable)
      return "x";

  return entry.minutes?.toString() ?? "0";
}

export const hasRole = (userRoles: string[] | string, targetRole: Role): boolean => {
  if (!Array.isArray(userRoles)) {
    userRoles = new Array(userRoles);
  }

  if (!userRoles || userRoles.length === 0)
    return false;

  return userRoles.some(x => x.toLowerCase() === targetRole.toLowerCase());
}

export const hasUserRole = (userRoles: UserRole[], targetRole: Role): boolean => {
  return userRoles.some(x => x.role.name.toLowerCase() === targetRole.toLowerCase());
}

/**
 * Returns an array of numbers representing the years between a start year and an end year (inclusive).
 *
 * @param {number} startYear - the starting year
 * @param {number} endYear - the ending year
 * @param {boolean} orderDesc - order the years in descending order
 * @return {number[]} an array of numbers representing the years between the start and end year (inclusive)
 */
export const getYearsBetween = (startYear: number, endYear: number, orderDesc = true): number[] => {
  const years = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  if (orderDesc) years.reverse();
  
  return years;
}

/**
 * Converts a given number of minutes to hours and minutes string.
 *
 * @param {number} minutes - The number of minutes to convert.
 * @return {string} The resulting string in the format "X hours, Y minutes" or "X hours" or "Y minutes" or "0 minutes".
 */
export const convertMinutesToHoursAndMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const hourString = hours > 0 ? `${hours}h` : "";
  const minuteString =
    remainingMinutes > 0
      ? `${remainingMinutes}m`
      : "";

  if (hourString && minuteString) {
    return `${hourString} ${minuteString}`;
  } else {
    return hourString || minuteString || "0m";
  }
}

/**
 * Clears the configuration settings in local storage
 */
export const clearLocalStorage = () => {
  localStorage.removeItem("discipline");
  localStorage.removeItem("disciplineId");
  localStorage.removeItem("cachedDisciplines");
  localStorage.removeItem("cachedDisciplinesTime");
}

/**
 * Converts a time string in the format of 'Xh Ymin' to total minutes.
 *
 * @param {string} timeString - The time string to be converted.
 * @return {number} The total number of minutes in the time string.
 */
export const convertTimeToMinutes = (timeString: string): number => {
  if (timeString.indexOf('h') === -1) return Number.parseInt(timeString);
  let [hours, minutes] = timeString.split('h ');
  minutes = minutes.replace('m', '');
  const totalMinutes = Number(hours) * 60 + Number(minutes.slice(0, -1));
  return totalMinutes;
};

/**
 * Removes HTML tags from a given string.
 *
 * @param {string} input - The string to be stripped of HTML tags.
 * @return {string} The input string with HTML tags removed.
 */
export const stripHtmlTags = (input: string): string => {
  return input.replace(/<[^>]+>/g, '');
}

/**
 * Removes special characters from a string.
 *
 * @param {string} input - The input string.
 * @return {string} The string with special characters removed.
 */
export const stripSpecialCharacters = (input: string, replacementCharacter: string = '-'): string => {
  return input.replace(/[^\w\s]/gi, replacementCharacter);
}

export const convertPageToZeroIndexing = (pageNumber: number): number => (pageNumber > 0) ? --pageNumber : 0


/**
 * Retrieves the billing type name based on the provided billing type ID.
 *
 * @param {number} billingTypeId - The ID of the billing type.
 * @return {string | undefined} The name of the billing type, or undefined if the ID is not valid.
 */
export const getBillingTypeName = (billingTypeId: number): string | undefined => {
  switch (billingTypeId) {
      case BillToLookup.School:
        return 'School';
      case BillToLookup.District:
        return 'District';
      default:
        return undefined;
  }
}


/**
 * Groups the task entries based on the specified task, entry state, and task types.
 *
 * @param {TimesheetWeekTask} task - The task to group the entries for.
 * @param {EntryState} entryState - The state of the entries.
 * @param {TaskType[]} taskTypes - The list of task types.
 * @return {TimesheetTaskEntries[]} The grouped task entries.
 */
export const groupTaskEntries = (task: TimesheetWeekTask, entryState: EntryState, taskTypes: TaskType[]): TimesheetTaskEntries[] => {
  const taskTypeEntries: TimesheetTaskEntries[] = [];
  const taskType = taskTypes.find(t => t.taskTypeId === task.taskTypeId);
  const tempEntries: DayEntry[] = [];
  const tempEntriesRecoupment: DayEntry[] = [];
  const tempEntriesMakeupTime: DayEntry[] = [];
  //If no task type was found then this may be a group entry so we skip it
  if (!taskType) {
      return [];
  }

  //Create a list of entries for each day of this week
  daysOfWeek(entryState.startDate).forEach((day) => {
      //Check for recoupment
      if (task.entries.filter(x => x.recoupment && !x.makeupTime).length > 0) {
          tempEntriesRecoupment.push({
              dateOfEntry: day,
              notes: "",
              minutes: undefined,
              enabled: true,
              recoupment: true,
              makeupTime: false,
              timesheetEntryId: undefined,
          });
      }

      //Check for makeup time
      if (task.entries.filter(x => !x.recoupment && x.makeupTime).length > 0) {
          tempEntriesMakeupTime.push({
              dateOfEntry: day,
              notes: "",
              minutes: undefined,
              enabled: true,
              makeupTime: true,
              timesheetEntryId: undefined,
          });
      }

      //Check for regular time
      if (task.entries.filter(x => !x.recoupment && !x.makeupTime).length > 0) {
          tempEntries.push({
              dateOfEntry: day,
              notes: "",
              minutes: undefined,
              enabled: true,
              recoupment: false,
              makeupTime: false,
              timesheetEntryId: undefined,
          });
      }
  });

  //Verify we have entries
  if (task.entries.length > 0) {
      //Loop through each entry
      task.entries.map(entry => {
          //Verify we have the specified index in the temp entries
          const tmpIndex = entry.recoupment ?
              tempEntriesRecoupment.findIndex(x => x.dateOfEntry.isSame(entry.dateOfEntry))
              :
              entry.makeupTime ?
                  tempEntriesMakeupTime.findIndex(x => x.dateOfEntry.isSame(entry.dateOfEntry))
                  :
                  tempEntries.findIndex(x => x.dateOfEntry.isSame(entry.dateOfEntry));

          //If so, set the correct data
          if (tmpIndex > -1) {
              //Check for recoupment
              if (entry.recoupment && !entry.makeupTime) {
                  tempEntriesRecoupment[tmpIndex].minutes = entry.minutes;
                  tempEntriesRecoupment[tmpIndex].notes = entry.notes;
                  tempEntriesRecoupment[tmpIndex].recoupment = entry.recoupment;
                  tempEntriesRecoupment[tmpIndex].timesheetEntryId = entry.entryId;
                  tempEntriesRecoupment[tmpIndex].billToId = entry.billToId;
                  tempEntriesRecoupment[tmpIndex].schoolId = entry.schoolId;
                  tempEntriesRecoupment[tmpIndex].recoupment = entry.recoupment;
                  tempEntriesRecoupment[tmpIndex].makeupTime = entry.makeupTime;
              }

              //Check for makeup time
              if (!entry.recoupment && entry.makeupTime) {
                  tempEntriesMakeupTime[tmpIndex].minutes = entry.minutes;
                  tempEntriesMakeupTime[tmpIndex].notes = entry.notes;
                  tempEntriesMakeupTime[tmpIndex].recoupment = entry.recoupment;
                  tempEntriesMakeupTime[tmpIndex].timesheetEntryId = entry.entryId;
                  tempEntriesMakeupTime[tmpIndex].billToId = entry.billToId;
                  tempEntriesMakeupTime[tmpIndex].schoolId = entry.schoolId;
                  tempEntriesMakeupTime[tmpIndex].recoupment = entry.recoupment;
                  tempEntriesMakeupTime[tmpIndex].makeupTime = entry.makeupTime;
              }

              //Check for regular time
              if (!entry.recoupment && !entry.makeupTime) {
                  tempEntries[tmpIndex].minutes = entry.minutes;
                  tempEntries[tmpIndex].notes = entry.notes;
                  tempEntries[tmpIndex].recoupment = entry.recoupment;
                  tempEntries[tmpIndex].timesheetEntryId = entry.entryId;
                  tempEntries[tmpIndex].billToId = entry.billToId;
                  tempEntries[tmpIndex].schoolId = entry.schoolId;
                  tempEntries[tmpIndex].recoupment = entry.recoupment;
                  tempEntries[tmpIndex].makeupTime = entry.makeupTime;
              }
          }

          return entry;
      });

      //Finally, add the task type to the task type entries array
      if (task.entries.some(x => x.recoupment && !x.makeupTime)) { //Recoupment
          taskTypeEntries.push({
              taskType: taskType,
              taskTypeId: task.taskTypeId,
              entries: tempEntriesRecoupment,
              startDate: entryState.startDate,
              schoolId: entryState.schoolId,
              endDate: entryState.endDate,
              isRecoupment: true,
              isMakeupTime: false
          });
      }

      //Makeup time
      if (task.entries.some(x => !x.recoupment && x.makeupTime)) {
          taskTypeEntries.push({
              taskType: taskType,
              taskTypeId: task.taskTypeId,
              entries: tempEntriesMakeupTime,
              startDate: entryState.startDate,
              schoolId: entryState.schoolId,
              endDate: entryState.endDate,
              isRecoupment: false,
              isMakeupTime: true
          });
      }

      //Regular time
      if (task.entries.some(x => !x.recoupment && !x.makeupTime)) {
          taskTypeEntries.push({
              taskType: taskType,
              taskTypeId: task.taskTypeId,
              entries: tempEntries,
              startDate: entryState.startDate,
              schoolId: entryState.schoolId,
              endDate: entryState.endDate,
              isRecoupment: false,
              isMakeupTime: false
          });
      }
  }

  return taskTypeEntries;
}

/**
 * Checks if the given entry is valid based on the entry tasks, index, and input minutes.
 *
 * @param {TimesheetTaskEntries[]} entryTasks - An array of entry tasks.
 * @param {number} index - The index of the entry to check.
 * @param {number} inputMinutes - The input minutes to compare against.
 * @return {boolean} Returns true if the entry is valid, false otherwise.
 */
export const isEntryValid = (entryTasks: TimesheetTaskEntries[] = [], index?: number, inputMinutes?: number): boolean => {
  let isValid = true;
  entryTasks.forEach(entryTask => {
      if (index && entryTask.entries[index] && (entryTask.entries[index] ?? 0) < (inputMinutes ?? 0)) {
          isValid = false;
      }
  });
  return isValid;
}

export type Option<T> = {
  label: string;
  value: T;
};

export const mapToOptions = <T, K extends keyof T, L extends keyof T>(
  data: T[],
  valueKey: K,
  labelKey: L
): Option<T[K]>[] => {
  return data.map((item) => ({
    value: item[valueKey],
    label: String(item[labelKey]),
  }));
};
